import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Icon,
  VStack,
  Text,
  Flex,
} from "@chakra-ui/react";
import { IoMdCheckmark } from "react-icons/io";
import { PiTimerDuotone } from "react-icons/pi";
import { SiBackendless } from "react-icons/si";
import StrongModal from "../common/StrongModal";
import StrongInput from "../common/StrongInput";
import StrongSelect from "../common/StrongSelect";
import StrongRichText from "../common/StrongRichText";
import StrongImageDragDrop from "../common/StrongImageDragDrop";
import StrongCheckBox from "../common/StrongCheckBox";

interface EditProgramInfoModalProps {
  selectedSection?: Section;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    title: string,
    type: string,
    pid: string,
    description: string,
    short_description: string,
    introVimeoId: string,
    purchaseLink: string,
    selectedequipments: string[],
    nutrition: string,
    selectedresources: string[],
    duration: string,
    cover: File | null,
    thumbnail: File | null,
    isFeatured: boolean,
    genders: string[],
  ) => void;
  equipmentstitle: EquipmentTitle[];
  resourcestitle: ResourceTitle[];
}

const EditProgramInfoModal = ({
  selectedSection,
  isOpen,
  onClose,
  onSubmit,
  equipmentstitle,
  resourcestitle,
}: EditProgramInfoModalProps) => {
  const [title, setTitle] = useState<string>("");
  const [pid, setPid] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [shortDescription, setShortDescription] = useState<string>("");
  const [duration, setDuration] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [introVimeoId, setIntroVimeoId] = useState<string>("");
  const [purchaseLink, setPurchaseLink] = useState<string>("");
  const [cover, setCover] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [isFeatured, setIsFeatured] = useState<boolean>(false);
  const [selTab, setSelTab] = useState<string>("fixed");
  const [nutrition, setNutrition] = useState<string>("");
  const [selectedresources, setSelectedResources] = useState<string[]>([]);
  const [selectedequipments, setSelectedEquipments] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);

  useEffect(() => {
    setTitle(selectedSection?.title || "");
    const nextType = selectedSection?.type || "fixed";
    setPid(selectedSection?.pid || "");
    setDescription(selectedSection?.description || "");
    setShortDescription(selectedSection?.short_description || "");
    setType(nextType);
    setSelTab(nextType); // important so UI matches edit value
    setDuration(selectedSection?.duration || "");
    setPurchaseLink(selectedSection?.purchaseLink || "");
    setCover(null);
    setThumbnail(null);
    setSelectedEquipments(selectedSection?.selectedequipments || []);
    setNutrition(selectedSection?.nutrition || "");
    setSelectedResources(selectedSection?.selectedresources || []);
    setIntroVimeoId(selectedSection?.introVimeoId || "");
    setIsFeatured(selectedSection?.isFeatured || false);
    setGenders(selectedSection?.genders || []);
  }, [selectedSection]);

  const onChangeTab = (select: string) => {
    setSelTab(select);
    setType(select);
    if (select === "ongoing") {
      setDuration("0");
    }
  };

  const finalDuration =
    selTab === "ongoing" ? "0" : duration === "" ? "0" : duration;

  return (
    <StrongModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() =>
        onSubmit(
          title,
          type,
          pid,
          description,
          shortDescription,
          introVimeoId,
          purchaseLink,
          selectedequipments,
          nutrition,
          selectedresources,
          finalDuration,
          cover,
          thumbnail,
          isFeatured,
          genders,
        )
      }
      title={selectedSection ? "Edit Program" : "Create Program"}
      isSubmitDisabled={!title}
      body={
        <VStack spacing={4} align="stretch">
          <FormControl>
            <FormLabel color="gray.600">Title</FormLabel>
            <StrongInput
              placeholder="Enter title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel color="gray.600">Product ID</FormLabel>
            <StrongInput
              placeholder="Enter Woocoomerce Product ID"
              value={pid}
              onChange={(e) => setPid(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel color="gray.600">Purchase Link</FormLabel>
            <StrongInput
              placeholder="Enter buy now / payment link"
              value={purchaseLink}
              onChange={(e) => setPurchaseLink(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel color="gray.600">Short Description</FormLabel>
            <StrongInput
              placeholder="Enter Short Description"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel color="gray.600">Program Overview</FormLabel>
            <Box bg="white">
              <StrongRichText
                placeholder="Enter Program Overview"
                value={description}
                onChange={setDescription}
              />
            </Box>
          </FormControl>
          <FormControl>
            <FormLabel color="gray.600">Program Type</FormLabel>
            <Box
              p="3"
              borderWidth="1px"
              borderColor={
                selTab === "fixed" ? "primary.300" : "border.disabled"
              }
              color="fg.disabled"
              onClick={() => {
                onChangeTab("fixed");
              }}
            >
              <Flex align="center" justify="space-between">
                <Flex align="center">
                  <Icon color="fg.disabled" mr="2">
                    <PiTimerDuotone size={25} />
                  </Icon>
                  <Text isTruncated>Fixed</Text>
                </Flex>
                {selTab === "fixed" && (
                  <Icon color="primary.500">
                    <IoMdCheckmark size={25} />
                  </Icon>
                )}
              </Flex>
            </Box>
            <Box
              p="3"
              mt="2"
              borderWidth="1px"
              borderColor={
                selTab == "ongoing" ? " primary.300" : "border.disabled"
              }
              color="fg.disabled"
              onClick={() => {
                onChangeTab("ongoing");
              }}
            >
              <Flex align="center" justify="space-between">
                <Flex align="center">
                  <Icon color="fg.disabled" mr="2">
                    <SiBackendless size={25} />
                  </Icon>
                  <Text isTruncated>Ongoing</Text>
                </Flex>
                {selTab == "ongoing" && (
                  <Icon color="primary.500">
                    <IoMdCheckmark size={25} />
                  </Icon>
                )}
              </Flex>
            </Box>
          </FormControl>
          {selTab === "fixed" && (
            <FormControl>
              <FormLabel color="gray.600">Duration</FormLabel>
              <Box mt="2" color="fg.disabled">
                <Flex align="center">
                  <StrongInput
                    placeholder="Enter Duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    type="number"
                  />
                  <Text ml={1}>Phases</Text>
                </Flex>
              </Box>
            </FormControl>
          )}

          <FormControl>
            <FormLabel color="gray.600">Cover</FormLabel>
            <Text fontSize="sm" color="gray.500" mb={2}>
              Ideal size: 1000 × 600 px (5:3). Other sizes will be displayed in
              cover mode without stretching or distortion.
            </Text>
            <StrongImageDragDrop
              onFileSelect={setCover}
              defaultImageUrl={selectedSection?.cover}
            />
          </FormControl>

          <FormControl>
            <FormLabel color="gray.600">Thumbnail</FormLabel>
            <Text fontSize="sm" color="gray.500" mb={2}>
              Ideal size: 1000 × 600 px (5:3). Other sizes will be displayed in
              cover mode without stretching or distortion.
            </Text>
            <StrongImageDragDrop
              onFileSelect={setThumbnail}
              defaultImageUrl={selectedSection?.thumbnail}
            />
          </FormControl>

          <FormControl>
            <FormLabel color="gray.600">Intro Video</FormLabel>
            <StrongInput
              placeholder="Enter Vimeo ID"
              value={introVimeoId}
              onChange={(e) => setIntroVimeoId(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel color="gray.600">Nutrition</FormLabel>
            <Box bg="white">
              <StrongRichText
                value={nutrition}
                onChange={setNutrition}
                placeholder="Enter nutrition content"
              />
            </Box>
          </FormControl>

          <FormControl>
            <FormLabel color="gray.600">Equipment</FormLabel>
            <StrongSelect
              options={
                equipmentstitle?.map(({ title, id }) => ({
                  label: title,
                  value: id,
                })) || []
              }
              value={selectedequipments.map((value) => {
                const equipment_title = equipmentstitle?.find(
                  (equipmenttile) => equipmenttile.id === value,
                ) as SectionTitle;
                return {
                  label: equipment_title.title,
                  value: equipment_title.id,
                };
              })}
              onSelectChange={setSelectedEquipments}
            />
          </FormControl>

          <FormControl>
            <FormLabel color="gray.600">Resources</FormLabel>
            <StrongSelect
              options={
                resourcestitle?.map(({ title, _id }) => ({
                  label: title,
                  value: _id,
                })) || []
              }
              value={selectedresources
                .map((value) => {
                  const resourceTitle = resourcestitle?.find(
                    (resource) => resource._id === value,
                  );
                  if (!resourceTitle) return null;

                  return {
                    label: resourceTitle.title,
                    value: resourceTitle._id,
                  };
                })
                .filter(
                  (
                    option,
                  ): option is {
                    label: string;
                    value: string;
                  } => option !== null,
                )}
              onSelectChange={setSelectedResources}
            />
          </FormControl>

          <FormControl>
            <FormLabel color="gray.600">Gender</FormLabel>
            <StrongSelect
              options={[
                { label: "Male", value: "male" },
                { label: "Female", value: "female" },
              ]}
              value={genders.map((g) => ({
                label: g.charAt(0).toUpperCase() + g.slice(1),
                value: g,
              }))}
              onSelectChange={setGenders}
            />
          </FormControl>

          <FormControl>
            <FormLabel color="gray.600">Featured</FormLabel>
            <StrongCheckBox
              isChecked={isFeatured}
              onCheckChange={setIsFeatured}
            />
          </FormControl>
        </VStack>
      }
    />
  );
};

export default EditProgramInfoModal;
